import React from "react";
import { createRoot } from "react-dom/client";
import "./styles/globals.css";
import { App } from "./App.js";
import { ErrorBoundary } from "./components/ErrorBoundary/index.js";

// ---------------------------------------------------------------------------
// PWA self-heal (bg-video stale-cache incident, 2026-06-10).
//
// Root cause chain of the "fixed twice, still broken" video bug:
//  1. The custom-domain zone serves /sw.js with Browser-Cache-TTL 4h, so a
//     deployed SW update can take hours to reach an existing player.
//  2. Until then the OLD SW serves the OLD precached app shell, whose bundle
//     requests the OLD video URL — which CacheFirst "video-cache" satisfies
//     with the corrupt pre-fix copy. The server-side fixes never reach them.
//  3. Even once the new SW installs (skipWaiting+claim), the already-loaded
//     page keeps running the old bundle: the stock registerSW.js has NO
//     controllerchange handler, so a SECOND manual refresh was required.
//
// Fixes here (client side, work regardless of zone config):
//  (a) controllerchange → one automatic reload, so the page re-renders on the
//      new app shell the moment a new SW takes control. Guarded so the very
//      first SW install (claim on an uncontrolled page) does NOT reload, and
//      it can fire at most once per page life.
//  (b) one-time purge: delete the entire legacy "video-cache" CacheStorage
//      bucket. BUG B fix (2026-06-13): /video/ is now NetworkOnly in the SW
//      (vite.config.ts) so iOS Range requests work; nothing writes video-cache
//      anymore. This purge evicts any populated video-cache left by an OLD
//      install (the SWR/CacheFirst era), whose full-200 cached body is exactly
//      what broke iOS Range playback. On fresh installs the bucket does not
//      exist → caches.delete is a harmless no-op.
// ---------------------------------------------------------------------------
if ("serviceWorker" in navigator) {
  let hadController = navigator.serviceWorker.controller !== null;
  let reloaded = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController) {
      // First-ever SW claim on this page — content came from the network and
      // is already current; no reload needed.
      hadController = true;
      return;
    }
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
}
if (typeof caches !== "undefined") {
  // Delete the legacy video-cache wholesale. /video/ is NetworkOnly now, so a
  // populated video-cache can only be stale leftovers from an old install whose
  // full-200 cached body broke iOS Range playback. No-op on fresh installs.
  caches.delete("video-cache").catch(() => {
    /* cache API unavailable (private mode etc.) — harmless */
  });
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found in DOM");

createRoot(rootEl).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);
